import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { Loading, Error, useQueryWithStore } from 'react-admin';
import {
  Grid,
  Table,
  Paper,
  Button,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  DialogActions,
  CircularProgress
} from '@material-ui/core/';
import ErrorIcon from '@material-ui/icons/ErrorOutline';
import withStyles from '@material-ui/core/styles/withStyles';
import capitalize from 'lodash/capitalize';
import TenantConfigurationApi from '../../../core/apis/TenantConfigurationApi';

import { FormSpy, Field } from 'react-final-form';
import CheckBoxField from '../../../core/components/CheckBoxField';
import SummaryStepStyles from './SummaryStepStyles';
import ResourceUploadApi from '../../../core/apis/ResourceUploadApi';
import dataProvider from '../../../core/providers/IccUserDataProvider';
import useInterval from '../../../core/hooks/UseInterval';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const MAX_COUNTER = 50; // 50 * 5 sec = 4.16 min

const DocumentTable = withStyles(SummaryStepStyles)(({ classes, ...props }) => {
  const {
    documents,
    resourceUploadId,
    allInvestors,
    tagName,
    useFundEmail,
    showSendEmail,
    defaultEmailAddress,
    sendEmailNotification
  } = props;

  const [fundIdToFundEmailMap, setFundIdToFundEmailMap] = useState({});

  useEffect(() => {
    if (showSendEmail && useFundEmail) {
      const uniqueFundIds = [...new Set(documents.map(doc => doc.fundId))];

      dataProvider('GET_MANY', 'funds', {
        ids: uniqueFundIds,
        target: 'id'
      }).then(({ data }) => {
        const fundIdToFundEmail = data.reduce((map, fund) => {
          map[fund.id] =
            fund.emailAddresses.length > 0
              ? fund.emailAddresses[0]
              : defaultEmailAddress;
          return map;
        }, {});
        setFundIdToFundEmailMap(fundIdToFundEmail);
      });
    }
  }, [showSendEmail, useFundEmail, documents, defaultEmailAddress]);

  const tableHeadCells = [
    { id: 'Document Title' },
    { id: 'Investors' },
    { id: 'Funds' },
    ...(showSendEmail && useFundEmail && sendEmailNotification
      ? [{ id: 'From Email Address' }]
      : []),
    ...(tagName ? [{ id: 'Document Tag' }] : []),
    { id: 'Category' },
    { id: 'Document Status' }
  ];
  const totalLabel =
    documents.length === 1
      ? documents.length + ' document'
      : documents.length + ' documents';
  return (
    <div>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.indicatorsContainer}>
          <span className={classes.batchIndicators}>Total: {totalLabel}</span>
          <span className={classes.batchIndicators}>
            Batch ID: {resourceUploadId}
          </span>
        </Typography>
        <div className={classes.tableWrapper}>
          <Paper>
            <Table>
              <EnhancedTableHead
                tableHeadCells={tableHeadCells}
                rowCount={documents.length}
              />
              <TableBody>
                <React.Fragment>
                  {documents.map(doc => {
                    return (
                      <TableRow key={doc.id}>
                        <TableCell id="Document Title">
                          {doc.documentTitle}
                        </TableCell>
                        <TableCell id="Investors">
                          {allInvestors ? 'All Investors' : doc.investorName}
                        </TableCell>
                        <TableCell id="Funds">{doc.fundName}</TableCell>
                        {showSendEmail &&
                        useFundEmail &&
                        sendEmailNotification ? (
                          <TableCell id="From Email Address">
                            {fundIdToFundEmailMap[doc.fundId]}
                          </TableCell>
                        ) : (
                          <></>
                        )}
                        {tagName ? (
                          <TableCell id="Document Tag">
                            <span className={classes.tag}>{tagName}</span>
                          </TableCell>
                        ) : (
                          <></>
                        )}
                        <TableCell id="Category">{doc.categoryName}</TableCell>
                        <TableCell id="Document Status">
                          {capitalize(doc.status)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              </TableBody>
            </Table>
          </Paper>
        </div>
      </Grid>
    </div>
  );
});

const ErrorBadge = withStyles(SummaryStepStyles)(({ classes, ...props }) => {
  return (
    <div className={classes.container}>
      <ErrorIcon className={classes.errorIcon} />
      <span className={classes.errorText}>Proof Error</span>
    </div>
  );
});

const EnhancedTableHead = props => {
  const { tableHeadCells } = props;

  return (
    <TableHead>
      <TableRow>
        {tableHeadCells.map(headCell => (
          <TableCell key={headCell.id} id={headCell.id}>
            {headCell.id}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export const SummaryActionBar = withStyles(SummaryStepStyles)(
  ({
    classes,
    handleProofActionChange,
    proofStatus,
    isPreviewLoading,
    showSendEmail,
    bulkMode,
    batchStatus,
    hasUnMatchedDocs = false,
    notificationCheckboxLabel,
    ...props
  }) => {
    return (
      <DialogActions className={classes.dialogActions}>
        <div className={classes.leftWrapper}>
          {showSendEmail && (
            <Field
              name="metadata.sendEmails"
              component={CheckBoxField}
              className={classes.checkbox}
              label={notificationCheckboxLabel}
              variant={props.variant}
              disabled={batchStatus === 'A'}
              type="checkbox"
            />
          )}
        </div>
        {bulkMode && proofStatus === 'ERROR' && <ErrorBadge />}

        {bulkMode && (
          <Button
            color="primary"
            value="Generate"
            variant="outlined"
            className={
              !proofStatus || proofStatus === 'PROCESSING'
                ? classes.disableButton
                : classes.button
            }
            onClick={e => handleProofActionChange(e.currentTarget.value)}
            disabled={
              !proofStatus || proofStatus === 'PROCESSING' || hasUnMatchedDocs
            }
          >
            {proofStatus !== 'PROCESSING' ? (
              'Generate PDF Proof'
            ) : (
              <>
                <CircularProgress size={14} className={classes.progressIcon} />
                <span className={classes.progressText}>Generating...</span>
              </>
            )}
          </Button>
        )}
        {bulkMode && (
          <Button
            color="primary"
            value="Review"
            variant="outlined"
            className={
              proofStatus !== 'COMPLETED'
                ? classes.disableButton
                : classes.button
            }
            disabled={
              isPreviewLoading ||
              proofStatus !== 'COMPLETED' ||
              hasUnMatchedDocs
            }
            onClick={e => handleProofActionChange(e.currentTarget.value)}
          >
            {!isPreviewLoading ? (
              'Preview PDF Proof'
            ) : (
              <>
                <CircularProgress size={14} className={classes.progressIcon} />
                <span className={classes.progressText}>Generating...</span>
              </>
            )}
          </Button>
        )}
      </DialogActions>
    );
  }
);

const SummaryStep = props => {
  const formRef = useRef(null);
  const lastEmailNotifyFlagRef = useRef(undefined);
  const lastResourceUploadIdRef = useRef(undefined);
  const [proofStatus, setProofStatus] = useState('');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [useFundEmail, setUseFundEmail] = useState(false);
  const [counter, setCounter] = useState(0);
  const [tagName, setTagName] = useState('');
  const notifyServerError = useNotifyServerError();
  const {
    resourceupload = {},
    hasSendEmailPermission,
    bulkMode,
    classes,
    onSubmitEnd,
    formName,
    onFormInit
  } = props;
  const {
    profileData = {},
    resourceUploadId = '',
    allInvestors = false,
    fundInvestorTagId
  } = resourceupload;
  const { categoryId = '' } = profileData;
  const [delay, setDelay] = useState(bulkMode ? 5000 : null);
  const [defaultEmailAddress, setDefaultEmailAddress] = useState('');
  const [notificationCheckboxLabel, setNotificationCheckBoxLabel] = useState(
    ''
  );

  const { loading, error, data } = useQueryWithStore({
    type: 'getManyReference',
    resource: 'resourceuploaddocuments',
    payload: {
      target: 'resourceUploadId',
      id: resourceUploadId,
      pagination: { page: 0, perPage: 10000 },
      sort: {},
      // adding an options parameter to trigger the query to be called once again
      options: { batchStatus: resourceupload.batchStatus }
    }
  });

  useEffect(() => {
    if (hasSendEmailPermission && data && data.length > 0 && categoryId) {
      const crudOperation = 'GET_ONE';
      dataProvider(crudOperation, 'categories', {
        id: categoryId
      })
        .then(({ data }) => {
          if (data) {
            setNotificationCheckBoxLabel(
              getNotificationCheckBoxLabel(data.triggerEmail)
            );
            setShowSendEmail(Number(data.triggerEmail) > 0);
            setTemplateId(data.templateId);
            if (templateId != null) {
              dataProvider(crudOperation, 'mailtemplatesonlyinfo', {
                id: templateId
              }).then(({ data }) => {
                if (data) {
                  setUseFundEmail(data.useFundEmail);
                }
              });
            }
          }
        })
        .catch(notifyServerError);
    }
  }, [
    hasSendEmailPermission,
    categoryId,
    data,
    notifyServerError,
    setShowSendEmail,
    templateId
  ]);

  // Note : for actual buisness requirement,
  // only one fund-investor tag can be assigned to a batch (step 2)
  // the same selected tag is assigned to all documents (step 3)
  // so we call only one time GET /v1/fundinvestortag?id=batchTagId to get the tag name
  useEffect(() => {
    TenantConfigurationApi.get('Administrator_CustomEmailAddress').then(
      response => {
        if (response?.pageData[0]?.value !== '') {
          setDefaultEmailAddress(response?.pageData[0]?.value);
        } else {
          TenantConfigurationApi.get('Administrator_Email').then(response => {
            if (response?.pageData[0]?.value !== '') {
              setDefaultEmailAddress(response?.pageData[0]?.value);
            }
          });
        }
      }
    );

    const crudOperation = 'GET_ONE';
    if (fundInvestorTagId) {
      dataProvider(crudOperation, 'fundinvestortags', {
        id: fundInvestorTagId
      })
        .then(({ data }) => setTagName(data.name))
        .catch(notifyServerError);
    }
  }, [fundInvestorTagId, notifyServerError]);

  const maxCounterReached = useMemo(() => {
    return counter >= MAX_COUNTER - 1;
  }, [counter]);

  const getNotificationCheckBoxLabel = triggerEmail => {
    let notificationCheckBoxLabel = '';
    switch (Number(triggerEmail)) {
      case 1:
        notificationCheckBoxLabel =
          'Send email notifications to regular contacts after the batch is published';
        break;
      case 2:
        notificationCheckBoxLabel =
          'Send email notifications to admin users after the batch is published';
        break;
      case 3:
        notificationCheckBoxLabel =
          'Send email notifications to all contacts (regular contacts and admin users) after the batch is published';
        break;
      default:
        notificationCheckBoxLabel = ''; // Handle unexpected values
    }
    return notificationCheckBoxLabel;
  };

  const getProofStatus = useCallback(async () => {
    setCounter(count => count + 1);
    try {
      const newProofStatus = await ResourceUploadApi.getProofStatus(
        resourceUploadId
      );
      if (
        (newProofStatus && newProofStatus.status !== 'PROCESSING') ||
        maxCounterReached
      ) {
        setDelay(null);
        setCounter(0);
      }
      setProofStatus(newProofStatus.status);
    } catch (error) {
      setDelay(null);
      setProofStatus('ERROR');
      notifyServerError(error);
    }
  }, [
    resourceUploadId,
    setProofStatus,
    maxCounterReached,
    setCounter,
    notifyServerError
  ]);

  useInterval(() => {
    if (bulkMode) {
      getProofStatus();
    }
  }, delay);

  const handleProof = async () => {
    setProofStatus('PROCESSING');
    try {
      await ResourceUploadApi.proof(resourceUploadId);
      setDelay(5000);
    } catch (error) {
      setProofStatus('ERROR');
      notifyServerError(error);
    }
  };

  /**
   * Download the proof file and open it in a new tab
   */
  const downloadProofFile = async () => {
    setIsPreviewLoading(true);
    try {
      const response = await ResourceUploadApi.downloadProofFile(
        resourceUploadId
      );
      setIsPreviewLoading(false);
      const url = window.URL.createObjectURL(response.data);
      window.open(url);
    } catch (error) {
      setIsPreviewLoading(false);
      notifyServerError(error);
    }
  };

  const handleProofActionChange = async value => {
    switch (value) {
      case 'Generate':
        await handleProof();
        break;
      case 'Review':
        await downloadProofFile();
        break;
      default:
        break;
    }
  };

  const approveDocuments = useCallback(
    async values => {
      if (onSubmitEnd) {
        try {
          await onSubmitEnd(resourceupload, values.metadata.sendEmails);
        } catch (error) {
          notifyServerError(error);
        }
      }
    },
    [onSubmitEnd, resourceupload, notifyServerError]
  );

  const persistedEmailNotifyFlag = Boolean(resourceupload?.emailNotifyFlag);

  useEffect(() => {
    if (loading || error) return;
    if (!formRef.current) return;

    if (resourceupload?.resourceUploadId !== lastResourceUploadIdRef.current) {
      lastResourceUploadIdRef.current = resourceupload?.resourceUploadId;
      lastEmailNotifyFlagRef.current = undefined;
    }

    if (persistedEmailNotifyFlag === lastEmailNotifyFlagRef.current) return;
    formRef.current.change('metadata.sendEmails', persistedEmailNotifyFlag);
    lastEmailNotifyFlagRef.current = persistedEmailNotifyFlag;
  }, [
    loading,
    error,
    resourceupload?.resourceUploadId,
    persistedEmailNotifyFlag
  ]);

  if (loading) return <Loading className={classes.loading} />;
  if (error) return <Error />;

  return (
    <CustomSimpleForm
      form={formName}
      save={approveDocuments}
      toolbar={null}
      resource="resourceuploaddocuments"
      initialValues={{
        metadata: {
          documents: [],
          sendEmails: persistedEmailNotifyFlag
        }
      }}
      variant="standard"
      margin="normal"
    >
      <FormSpy subscription={{ pristine: true }}>
        {formprops => {
          formRef.current = formprops.form;
          if (onFormInit !== undefined) {
            onFormInit(formprops.form);
          }
          const hasUnMatchedDocs =
            data.findIndex(doc => doc.matchingStatus === 'MISMATCH') !== -1;
          return (
            <React.Fragment>
              <DocumentTable
                documents={data}
                tagName={tagName}
                resource="resourceuploaddocuments"
                resourceUploadId={resourceUploadId}
                bulkMode={bulkMode}
                allInvestors={allInvestors}
                useFundEmail={useFundEmail}
                showSendEmail={showSendEmail}
                defaultEmailAddress={defaultEmailAddress}
                sendEmailNotification={!formprops.pristine}
              />
              {data.length > 0 && (
                <SummaryActionBar
                  handleProofActionChange={handleProofActionChange}
                  proofStatus={proofStatus}
                  isPreviewLoading={isPreviewLoading}
                  showSendEmail={showSendEmail}
                  bulkMode={bulkMode}
                  batchStatus={resourceupload.batchStatus}
                  hasUnMatchedDocs={hasUnMatchedDocs}
                  notificationCheckboxLabel={notificationCheckboxLabel}
                />
              )}
            </React.Fragment>
          );
        }}
      </FormSpy>
    </CustomSimpleForm>
  );
};

export default withStyles(SummaryStepStyles)(SummaryStep);
